import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import {
  ArrowRight,
  Download,
  Filter,
  GitMerge,
  Layers,
  Percent,
  Users
} from 'lucide-react';
import React from 'react';
import {
  ChatAvatar,
  ChatBody,
  ChatHeader,
  ChatHeaderName,
  ChatHeaderStatus,
  ChatWindow,
  UserBubble,
  UserBubbleRow
} from '../../components/ChatMockup';
import { DemoButton } from '../../components/DemoButton';
import { Headline } from '../../components/Headline';
import { mobileLeadText } from '../../components/MarketingTypography';
import { Kicker } from '../../components/Kicker';
import { Reveal } from '../../components/motion';
import PageHead from '../../components/PageHead';
import { ProductIcon } from '../../components/ProductIcon';
import { SEO } from '../../components/SEO';
import {
  DataAccessMockup,
  IntegrationPipelineMockup,
  RevenueWorkspaceMockup,
  WarehouseIntegrationsMockup
} from '../../components/UnifyProductMockups';
import MainLayout from '../../layouts/main';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const INK = COLORS.INK;

const StorySection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const StoryVisual = styled('div')`
  min-width: 0;

  @media (max-width: 960px) {
    order: -1;
  }
`;

const StoryContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StoryReveal = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const CenteredStoryReveal = styled(StoryReveal)`
  align-items: center;
  text-align: center;
`;

const StoryBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
  ${mobileLeadText}
`;

const ProductStorySection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 96px;
  padding: 0 24px;
`;

const ProductStoryHeader = styled('div')`
  max-width: 720px;
  margin: 0 auto 40px;
  text-align: center;
`;

const ProductStoryBody = styled('p')`
  margin: 0 auto;
  color: #4f4f4f;
  font-size: 17px;
  line-height: 1.65;
`;

const FeaturesSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const FeaturesHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const FeaturesIntro = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
`;

const BentoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const BentoCard = styled('div')`
  height: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const BentoTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

const BentoBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

const AgentSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const EXAMPLE_PROMPTS = [
  'Which key merchants dropped commission rates this month?',
  'Which partners are behind on payments?',
  "What's driving performance for my top merchants?"
] as const;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const FinalCtaBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
  ${mobileLeadText}
`;

const BENTO_FEATURES = [
  {
    icon: Percent,
    title: 'Unified partnerships and rates',
    body:
      'One report for partnerships, rates, and categories across networks. Catch merchant network moves, rate changes, and program shutdowns without checking each dashboard.'
  },
  {
    icon: GitMerge,
    title: 'Multiple accounts per network',
    body:
      'Connect every account and property you operate, then report across the portfolio without logging into each network separately.'
  },
  {
    icon: Layers,
    title: 'Isolate bonus payments',
    body:
      'Separate bonus payments from standard commissions and classify each line as bonus, CPA, CPC, and other network-specific types. Report on the metrics that match how each program actually pays.'
  },
  {
    icon: Filter,
    title: 'Custom reports and export',
    body:
      'Filter, group, and sort with presets or your own combinations, then export to CSV or XLSX to easily share with external stakeholders.'
  },
  {
    icon: Download,
    title: 'Full lifecycle from click to payout',
    body:
      'Follow each commission from the originating click through purchase, lock, and payout when you need to explain a discrepancy or pending balance.'
  },
  {
    icon: Users,
    title: 'Access controls and permissions',
    body:
      'Robust role-based permissions and custom roles give each team member access to just the reports and exports they need without sharing network logins.'
  }
] as const;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/unify/"
          title="Affilimate Unify: One Trusted Source for Affiliate Data"
          description="Aggregate affiliate transactions, rates, finance data, and payouts from 100+ networks into one reliable record for reporting, reconciliation, Agent, MCP, APIs, and warehouse exports."
        />

        <PageHead>
          <Reveal>
            <Headline
              variant="h1"
              gutterBottom
              component="h1"
              style={{
                maxWidth: '800px',
                margin: '0 auto 24px',
                fontWeight: 900,
                display: 'block'
              }}
            >
              All your affiliate data in one place.
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: '620px', margin: '24px auto 12px' }}
            >
              Unify normalizes transactions, finance data, rates, and more from
              100+ networks into a streamlined format your team can actually
              report from.
            </Typography>
            <div style={{ textAlign: 'center', margin: '48px auto 0' }}>
              <DemoButton text="Get a demo" variant="contained" />
            </div>
          </Reveal>
        </PageHead>

        <ProductStorySection>
          <ProductStoryHeader>
            <Reveal>
              <CenteredStoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Unified commissions</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Revenue intelligence at the speed of thought
                </Headline>
                <ProductStoryBody>
                  Revenue data that loads so fast, you'll enjoy working with it.
                  Get cross-network insights that load instantly in
                  Affilimate&apos;s web app, or connect the same record to your
                  warehouse and BI stack when teams need to marry it with other
                  sources.
                </ProductStoryBody>
              </CenteredStoryReveal>
            </Reveal>
          </ProductStoryHeader>
          <Reveal delay={120}>
            <RevenueWorkspaceMockup />
          </Reveal>
        </ProductStorySection>

        <StorySection>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Integrations</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Connect 100+ affiliate networks without a developer
                </Headline>
                <StoryBody>
                  Monitor commissions and run cross-cutting reports across every
                  connected network, aggregator, and SaaS affiliate tool. One
                  workspace for the accounts that power each property in your
                  portfolio.
                </StoryBody>
                <StoryBody>
                  Easy, step-by-step guides means anyone from the team with
                  access can get connected.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <IntegrationPipelineMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <StorySection flip>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Data access</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  One source for every way your team works
                </Headline>
                <StoryBody>
                  Finance uses payout tools. Ops investigates delivery and
                  revenue movement. BI and Data pull the same records into their
                  stack and cross-check with the UI. One source of truth,
                  whichever team starts the workflow.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <DataAccessMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <StorySection>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Warehouse exports</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Push enriched commerce data into your systems and tools
                </Headline>
                <StoryBody>
                  Push unified commerce data into your warehouse and join it
                  with the ad, audience, and subscriber signals you already
                  have. Expertly created integration paths mean pulling data
                  into your system is a few clicks away.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <WarehouseIntegrationsMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <FeaturesSection>
          <Reveal>
            <FeaturesHeader>
              <CenteredStoryReveal>
                <Kicker style={{ marginBottom: 0 }}>
                  Built for commerce teams
                </Kicker>
                <Headline component="h2" style={{ margin: 0 }}>
                  Reporting depth without the spreadsheet work
                </Headline>
                <FeaturesIntro>
                  Stop stitching together network exports or doing internal
                  support on every network discrepancy. Teams get one place to
                  report, attribute commissions, trace finance data, and export
                  across every connected network.
                </FeaturesIntro>
              </CenteredStoryReveal>
            </FeaturesHeader>
          </Reveal>
          <BentoGrid>
            {BENTO_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Reveal
                  key={feature.title}
                  delay={index * 60}
                  style={{ height: '100%' }}
                >
                  <BentoCard>
                    <ProductIcon>
                      <Icon size={20} />
                    </ProductIcon>
                    <BentoTitle>{feature.title}</BentoTitle>
                    <BentoBody>{feature.body}</BentoBody>
                  </BentoCard>
                </Reveal>
              );
            })}
          </BentoGrid>
        </FeaturesSection>

        <AgentSection>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Early access</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Answer earnings questions without building another report
                </Headline>
                <StoryBody>
                  Ask why earnings moved, what is still pending payout, or how a
                  rate change hit your top pages. Agent and MCP connect to the
                  same unified record in Claude, ChatGPT, and your other tools,
                  with no exports and no code.
                </StoryBody>
                <InlineLink to="/mcp/">
                  Explore Affilimate MCP <ArrowRight size={18} />
                </InlineLink>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <Reveal delay={150}>
            <ChatWindow aria-hidden>
              <ChatHeader>
                <ChatAvatar>
                  <img src="/favicon.png" alt="" />
                </ChatAvatar>
                <ChatHeaderName>Affilimate Agent</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                {EXAMPLE_PROMPTS.map((prompt) => (
                  <UserBubbleRow key={prompt}>
                    <UserBubble>{prompt}</UserBubble>
                  </UserBubbleRow>
                ))}
              </ChatBody>
            </ChatWindow>
          </Reveal>
        </AgentSection>

        <FinalCta>
          <Reveal>
            <CenteredStoryReveal>
              <Headline component="h2" style={{ margin: 0 }}>
                See Unify on your affiliate data
              </Headline>
              <FinalCtaBody>
                Get a demo tailored to your networks and workflows, then set up
                in minutes without developer resources.
              </FinalCtaBody>
              <DemoButton text="Get a demo" variant="contained" />
            </CenteredStoryReveal>
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
